import { Controller } from "@hotwired/stimulus"
import {hideElement, showElement} from 'utils/toggle_element_visibility'

////
// Since we only care about file drag and drop functionality, we don't need a
// whole library just to support that. Most filepickers are much more feature rich
// and therefore much more code - and they are often more difficult to customize.
// This simple drag and drop file picker only allows one file, and doesn't handle
// any 'previewing' of data other than the file name.

// Example HTML (Slim) for this component:
//
// div.c-file-drop data-controller="file-drop" data-file-drop-acceptable-extensions-value="doc,docx,pdf,png,jpeg,jpg" data-action="dragenter->file-drop#dragenter dragover->file-drop#dragover dragleave->file-drop#dragleave dragend->file-drop#dragend  drop->file-drop#drop"
//   = f.file_field :score_report, required: true, data: { "target": "file-drop.input", "action": "change -> file-drop#fileChange" }
//   = f.label :score_report, "Drag and drop file or click to browse"
//   div.c-file-drop-errors data-file-drop-target="errorContainer"

//   div.btn.btn-primary.btn-xs.btn-clear-file data-file-drop-target="clearFile" = qb_icon("times")
//   div.qb-file-preview
//     div data-file-drop-target="filePreviewName"

export default class extends Controller {
  static targets = [ "input", "inputLabel", "filePreviewName", "errorContainer", "errorText", "clearFile"]

  static values = {
    acceptableExtensions: String
  }

  initialize(){
    this.$form = this.inputTarget.closest("form")
    this.$submitBtn = this.$form.querySelector("input[type='submit'], button[type='submit']")
    this.$finePrint = this.$form.querySelector("[data-file-drop-fine-print]")
    this.required = this.inputTarget.required
    // since we hide the input, we can't make it required, but we can enforce validation via JS.
    this.inputTarget.required = false

    this.$form.addEventListener("submit", (e) => {
      if(this.required && this.inputTarget.files.length === 0) {
        this._addError("This field is required")
        e.preventDefault()
      }
    })

    if(this.hasClearFileTarget) {
      this.clearFileTarget.addEventListener("click", () => {
        this.inputTarget.value = ""
        this._onFilesCleared()
      })
    }

    this.acceptableExtensions = this.acceptableExtensionsValue.split(",")
  }

  fileChange(e) {
    if(this.inputTarget.files.length > 0) {
      this._onFilesAdded(this.inputTarget.files)
    } else {
      this._onFilesCleared()
    }
  }

  dragenter(e) {
    e.stopPropagation()
    e.preventDefault()
    this._addHoverEffect()
  }

  dragover(e) {
    e.stopPropagation()
    e.preventDefault()
    this._addHoverEffect()
  }

  dragleave(e) {
    e.stopPropagation()
    e.preventDefault()
    this._removeHoverEffect()
  }

  dragend(e) {
    e.stopPropagation()
    e.preventDefault()
    this._removeHoverEffect()
  }

  drop(e) {
    this._removeHoverEffect()
    e.stopPropagation()
    e.preventDefault()

    const dt = e.dataTransfer
    const files = dt.files

    this.element.querySelector("input[type=file]").files = files
    this._onFilesAdded(files)
  }

  _addHoverEffect(){
    // remove below when FileDropComponent is used on every page
    this.element.classList.add("file-drag-hover")
    // remove above when FileDropComponent is used on every page
    this.element.classList.add("-hover")
  }

  _removeHoverEffect(){
    // remove below when FileDropComponent is used on every page
    this.element.classList.remove("file-drag-hover")
    // remove above when FileDropComponent is used on every page
    this.element.classList.remove("-hover")
  }

  _onFilesAdded(files){
    if(this.acceptableExtensions){
      const checkFileExtensionResult = this._checkAcceptableFileExtensions(files)
      if(!checkFileExtensionResult) { return }
    }
    this.element.classList.add("containing-file")
    hideElement(this.$finePrint)
    if(this.hasFilePreviewNameTarget) {
      this.filePreviewNameTarget.innerText = files[0].name
    }
    this.emitFilesAdded(files)
  }

  _onFilesCleared(){
    this.element.classList.remove("containing-file")
    showElement(this.$finePrint)
    if(this.hasFilePreviewNameTarget) {
      this.filePreviewNameTarget.innerText = ""
    }
  }

  emitFilesAdded(files) {
    const event = new CustomEvent('file-drop.filesadded', { bubbles: true, detail: { files: files } })
    this.element.dispatchEvent(event)
  }

  _checkAcceptableFileExtensions(files){
    this._clearErrors()
    let allGood = true
    Array.from(files).forEach((f)=>{
      const arr = f.name.split(".")
      const ext = arr[arr.length - 1]
      if(this.acceptableExtensions.indexOf(ext) === -1) {
        this._addError(`Can't accept files with extension ${ext}. Accepted files: ${this.acceptableExtensions.join(', ')}.`)
        allGood = false
      }
    })
    return allGood
  }

  _clearErrors(){
    hideElement(this.errorContainerTarget)

    // legacy support for file drops that don't have the errorTextTarget defined yet.
    if(this.hasErrorTextTarget) {
      this.errorTextTarget.innerHTML = ""
    } else {
      this.errorContainerTarget.innerHTML = ""
    }
    this.$submitBtn.disabled = false
  }

  _addError(msg){
    showElement(this.errorContainerTarget)
    this.$submitBtn.disabled = true;
    // legacy support for file drops that don't have the errorTextTarget defined yet.
    (this.errorTextTarget || this.errorContainerTarget).innerHTML += `<div class='file-error-message'>${msg}</div>`
  }
}
